import { api } from "../config";
import { getCurrentLanguage } from "../i18n";

const PRODUCT_FEEDBACK_URL = "product-feedback";

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SHORT_TEXT_MAX = 180;

const asUuidOrNull = (value) => {
    if (!value) return null;
    const text = String(value);
    return UUID_RE.test(text) ? text : null;
};

const shortTextOrNull = (value) => {
    if (value === null || value === undefined) return null;
    const text = String(value).replace(/\s+/g, " ").trim();
    if (!text) return null;
    return text.slice(0, SHORT_TEXT_MAX);
};

const compactContext = (context = {}) => {
    const allowed = {};
    [
        "source",
        "route",
        "pathname",
        "feature_area",
        "target_type",
        "target_id",
        "analyse_id",
        "document_version_id",
        "ai_comment_id",
        "client_language",
        "viewport",
        "user_agent",
        "timestamp_client",
        // contexte commentaire / mémo
        "risk_level",
        "comment_title",
        "criterion_id",
        "criterion_title",
        "comment_type",
        "section",
        "memo_status",
        "enrichment_status",
        // Contract-profile context only; never include contract text.
        "contract_profile_status",
        "contract_profile_section",
        // contexte global
        "analysis_running",
        "right_panel_mode",
        "is_create",
        // auto-capture technique
        "error_kind",
        "error_message",
        "stream_phase",
        "comparison_kind",
    ].forEach((key) => {
        if (context[key] !== undefined && context[key] !== null && context[key] !== "") {
            allowed[key] = context[key];
        }
    });

    if (allowed.comment_title) {
        allowed.comment_title = shortTextOrNull(allowed.comment_title);
    }
    if (allowed.criterion_title) {
        allowed.criterion_title = shortTextOrNull(allowed.criterion_title);
    }
    if (allowed.section) {
        allowed.section = shortTextOrNull(allowed.section);
    }
    if (allowed.contract_profile_section) {
        allowed.contract_profile_section = shortTextOrNull(allowed.contract_profile_section);
    }
    if (allowed.error_message) {
        allowed.error_message = shortTextOrNull(allowed.error_message);
    }
    if (allowed.risk_level) {
        allowed.risk_level = shortTextOrNull(allowed.risk_level);
    }
    if (allowed.user_agent) {
        allowed.user_agent = shortTextOrNull(allowed.user_agent);
    }

    return allowed;
};

export const createProductFeedback = async (payload = {}) => {
    const pageRoute = payload.page_route || `${window.location.pathname}${window.location.search || ""}`;
    const pathname = window.location.pathname;
    const featureArea = payload.feature_area || "global_review";
    const targetType = payload.target_type || null;
    const targetId = shortTextOrNull(payload.target_id);
    const analyseId = asUuidOrNull(payload.analyse_id);
    const documentVersionId = asUuidOrNull(payload.document_version_id);
    const aiCommentId = asUuidOrNull(payload.ai_comment_id);

    const body = {
        page_route: pageRoute,
        workspace_id: asUuidOrNull(payload.workspace_id),
        analyse_id: analyseId,
        document_version_id: documentVersionId,
        ai_comment_id: aiCommentId,
        feature_area: featureArea,
        target_type: targetType,
        target_id: targetId,
        interaction_type: payload.interaction_type || "manual",
        sentiment: payload.sentiment || null,
        reason: payload.reason || null,
        message: payload.message?.trim() || null,
        context_json: compactContext({
            ...(payload.context_json || {}),
            source: payload.context_json?.source || "global_feedback_drawer",
            route: pageRoute,
            pathname,
            feature_area: featureArea,
            target_type: targetType,
            target_id: targetId,
            analyse_id: analyseId,
            document_version_id: documentVersionId,
            ai_comment_id: aiCommentId,
            client_language: payload.context_json?.client_language || getCurrentLanguage?.(),
            viewport: {
                width: window.innerWidth,
                height: window.innerHeight,
            },
            user_agent: window.navigator?.userAgent || null,
            timestamp_client: new Date().toISOString(),
        }),
    };

    return api.post(PRODUCT_FEEDBACK_URL, body, { timeout: 5000 });
};

/**
 * Auto-capture pour erreurs techniques uniquement (transport_error, ttft_timeout,
 * first_card_timeout, analysis_failed). Best-effort, fire-and-forget, jamais bloquant.
 * Throttle in-memory : 1 envoi par session par errorKind.
 */
const AUTO_CAPTURE_KIND_TO_REASON = {
    transport_error: "Erreur réseau / SSE",
    ttft_timeout: "Trop lent",
    first_card_timeout: "Trop lent",
    analysis_failed: "Bug d'interface",
};

const sentAutoKinds = new Set();

export const reportTechnicalAutoFeedback = ({
    errorKind,
    analyseId = null,
    documentVersionId = null,
    workspaceId = null,
    aiCommentId = null,
    featureArea = "analysis_stream",
    targetType = "analysis_stream",
    targetId = null,
    streamPhase = null,
    errorMessage = null,
    extraContext = null,
} = {}) => {
    try {
        if (!errorKind) return;
        if (sentAutoKinds.has(errorKind)) return;
        sentAutoKinds.add(errorKind);

        const reason = AUTO_CAPTURE_KIND_TO_REASON[errorKind] || "Autre";

        // fire-and-forget
        createProductFeedback({
            workspace_id: workspaceId,
            analyse_id: analyseId,
            document_version_id: documentVersionId,
            ai_comment_id: aiCommentId,
            feature_area: featureArea,
            target_type: targetType,
            target_id: targetId,
            interaction_type: "auto",
            sentiment: "negative",
            reason,
            message: null,
            context_json: {
                source: "auto_capture",
                error_kind: errorKind,
                stream_phase: streamPhase,
                error_message: errorMessage,
                ...(extraContext || {}),
            },
        }).catch(() => null);
    } catch {
        /* best-effort, ne jamais lever */
    }
};

export const fetchProductFeedback = async (params = {}) => {
    const response = await api.get(PRODUCT_FEEDBACK_URL, { params });
    return response.data;
};

export const updateProductFeedbackStatus = async (feedbackId, status) => {
    const response = await api.patch(`${PRODUCT_FEEDBACK_URL}/${feedbackId}/status`, { status });
    return response.data;
};

export const deleteProductFeedback = async (feedbackId) => {
    const response = await api.delete(`${PRODUCT_FEEDBACK_URL}/${feedbackId}`);
    return response.data;
};
